// frontend/src/components/DailyRoleAssignmentPanel.jsx
// 日付ごとの役割割当パネル (リーダー等)
// - 役割マスタを取得
// - その日の日勤者一覧を取得 (役割の day_shift_marks に該当する shift を持つ人)
// - 各役割の枠で max_count 名まで選択
// - 保存

import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { supabase } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";

const DailyRoleAssignmentPanel = ({ selectedDate }) => {
  const { currentOrganization } = useOrganization();
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [dayShiftByStaff, setDayShiftByStaff] = useState({});  // empId → display_mark
  const [assignments, setAssignments] = useState({}); // role_id → staff_ids[]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const dateStr = useMemo(() =>
    dayjs(selectedDate || new Date()).format("YYYY-MM-DD"),
    [selectedDate]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [rolesRes, stfRes, shiftRes] = await Promise.all([
          supabase.from("daily_role_definitions")
            .select("*")
            .eq("organization_id", currentOrganization.id)
            .eq("is_active", true)
            .order("display_order"),
          supabase.from("staff")
            .select("id, employee_id, last_name, first_name")
            .eq("organization_id", currentOrganization.id)
            .eq("is_active", true),
          supabase.from("monthly_shifts")
            .select("employee_id, shift_type")
            .eq("organization_id", currentOrganization.id)
            .eq("shift_date", dateStr),
        ]);
        setRoles(rolesRes.data || []);
        setStaffList(stfRes.data || []);
        const shiftMap = {};
        (shiftRes.data || []).forEach((s) => { shiftMap[s.employee_id] = s.shift_type; });
        setDayShiftByStaff(shiftMap);

        // 既存の割当を取得
        const { data: asgData } = await supabase.from("daily_role_assignments")
          .select("role_definition_id, staff_ids")
          .eq("organization_id", currentOrganization.id)
          .eq("assignment_date", dateStr);
        const asgMap = {};
        (asgData || []).forEach((a) => {
          asgMap[a.role_definition_id] = Array.isArray(a.staff_ids) ? a.staff_ids : [];
        });
        setAssignments(asgMap);
      } catch (e) {
        console.error("daily role panel 取得失敗:", e);
        setError(`取得エラー: ${e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentOrganization?.id, dateStr]);

  // employee_id → staff.id への変換マップ (assignments には staff.id 配列で保存する)
  const empIdToStaffId = useMemo(() =>
    Object.fromEntries(staffList.map((s) => [s.employee_id, s.id])),
    [staffList]);
  const staffIdToEmpId = useMemo(() =>
    Object.fromEntries(staffList.map((s) => [s.id, s.employee_id])),
    [staffList]);
  const staffNameMap = useMemo(() =>
    Object.fromEntries(staffList.map((s) =>
      [s.id, `${s.last_name || ""} ${s.first_name || ""}`.trim() || s.employee_id])),
    [staffList]);

  const candidatesForRole = (role) => {
    const marks = Array.isArray(role.day_shift_marks) ? role.day_shift_marks : [];
    if (marks.length === 0) {
      // マーク未設定 → 全スタッフを候補にする (緩い解釈)
      return staffList;
    }
    return staffList.filter((s) => marks.includes(dayShiftByStaff[s.employee_id]));
  };

  const toggleAssign = (roleId, staffId, maxCount) => {
    setAssignments((prev) => {
      const cur = new Set(prev[roleId] || []);
      if (cur.has(staffId)) {
        cur.delete(staffId);
      } else {
        if (cur.size >= maxCount) {
          alert(`この役割の最大人数は ${maxCount} 名です`);
          return prev;
        }
        cur.add(staffId);
      }
      return { ...prev, [roleId]: [...cur] };
    });
  };

  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!currentOrganization?.id) return;
    setSaving(true);
    try {
      // upsert 全役割
      const rows = roles.map((r) => ({
        organization_id: currentOrganization.id,
        role_definition_id: r.id,
        assignment_date: dateStr,
        staff_ids: assignments[r.id] || [],
        updated_at: new Date().toISOString(),
      }));
      if (rows.length === 0) {
        setSuccess("保存対象がありません");
        return;
      }
      const { error: e } = await supabase
        .from("daily_role_assignments")
        .upsert(rows, { onConflict: "organization_id,role_definition_id,assignment_date" });
      if (e) throw e;
      setSuccess(`✅ ${dateStr} の役割割当を保存しました`);
    } catch (e) {
      console.error("保存失敗:", e);
      setError(`保存エラー: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>読み込み中...</div>;
  }
  if (roles.length === 0) {
    return (
      <div style={{
        padding: 16, background: "#fef9c3", color: "#854d0e",
        borderRadius: 8, fontSize: 13,
      }}>
        💡 役割マスタが登録されていません。 <strong>設定 → 日次役割マスタ</strong> から追加してください。
      </div>
    );
  }

  return (
    <div style={{
      background: "white", borderRadius: 12, padding: 16, border: "1px solid #e5e7eb",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          📌 {dateStr} の日次役割
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {success && <span style={{ color: "#166534", fontSize: 11 }}>{success}</span>}
          {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
          <button onClick={handleSave} disabled={saving} style={{
            padding: "6px 14px",
            background: saving ? "#94a3b8" : "linear-gradient(135deg, #1d4ed8, #1e3a8a)",
            color: "white", border: "none", borderRadius: 6,
            fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "保存中..." : "💾 保存"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {roles.map((role) => {
          const candidates = candidatesForRole(role);
          const selected = assignments[role.id] || [];
          return (
            <div key={role.id} style={{
              border: `2px solid ${role.color || "#3b82f6"}40`,
              borderRadius: 10,
              padding: 12,
              background: `${role.color || "#3b82f6"}06`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: role.color || "#3b82f6" }}>
                  {role.name}
                  <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>
                    ({selected.length}/{role.max_count}名)
                  </span>
                </div>
              </div>
              {/* 選択中 */}
              {selected.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                  {selected.map((sid) => (
                    <span key={sid} style={{
                      padding: "3px 8px", borderRadius: 12, fontSize: 11,
                      background: role.color || "#3b82f6", color: "white", fontWeight: 600,
                    }}>
                      {staffNameMap[sid] || "(不明)"}
                      <button onClick={() => toggleAssign(role.id, sid, role.max_count)} style={{
                        marginLeft: 4, background: "transparent", border: "none",
                        color: "white", cursor: "pointer", padding: 0, fontSize: 10,
                      }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {/* 候補 */}
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                {candidates.length === 0
                  ? "候補なし (日勤者がいません)"
                  : `候補: ${candidates.length}名`}
              </div>
              <div style={{ maxHeight: 160, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                {candidates.map((s) => {
                  const isSelected = selected.includes(s.id);
                  return (
                    <button key={s.id} onClick={() => toggleAssign(role.id, s.id, role.max_count)} style={{
                      padding: "5px 8px", borderRadius: 6, fontSize: 12, textAlign: "left",
                      background: isSelected ? `${role.color || "#3b82f6"}22` : "white",
                      border: isSelected ? `1px solid ${role.color || "#3b82f6"}` : "1px solid #e5e7eb",
                      color: isSelected ? role.color || "#3b82f6" : "#0f172a",
                      fontWeight: isSelected ? 600 : 400,
                      cursor: "pointer",
                    }}>
                      {isSelected ? "✓ " : ""}{s.last_name} {s.first_name}
                      <span style={{ fontSize: 9, color: "#94a3b8", marginLeft: 4 }}>
                        ({dayShiftByStaff[s.employee_id] || "?"})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyRoleAssignmentPanel;
